import React, { useEffect, useMemo, useRef, useState } from 'react';

import axios from 'axios';
import classNames from 'classnames';
import { Formik } from 'formik';
import * as Yup from 'yup';

import {
    PROFILE_MONTHLY,
    PROFILE_QUARTERLY,
    PROFILE_SINGLE,
    allocation,
    buildProfile,
    chunk,
    clampMonth,
    displayPercentage,
    isExactly100,
    rowPercentageError,
    rowsInRange,
    scaleTo100,
    sortRows,
    valueToPercentage,
    valuesForRows,
    withoutZeroRows,
} from './phasingMath';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import ModalHeader from 'CommonComponents/ModalHeader';
import SegmentedControl from 'CommonComponents/SegmentedControl';

const TYPE_CASH = 'cash';
const TYPE_VIK = 'vik';

/**
 * Lines per request in a multi-line save. A line inside a large project recalculates
 * the whole project, so a batch is kept well inside one request's memory and time
 * limits. Matches the server's cap (UpdateExpenseCashflowPhasingRequest::MAX_LINES).
 */
const SAVE_BATCH_SIZE = 50;

const SKIP_REASON_LABELS = {
    type_mismatch: () => $t('Different cash/VIK type'),
    deleted: () => $t('Line no longer exists'),
    fully_committed: () => $t('Fully committed'),
    fully_allocated: () => $t('Fully allocated to project lines'),
    contingency: () => $t('Contingency lines are not phased'),
    forbidden: () => $t('You cannot edit this line'),
};

const ROW_ERROR_LABELS = {
    invalid: () => $t('Enter a number.'),
    negative: () => $t('Cannot be negative.'),
    over: () => $t('Cannot be more than 100%.'),
};

function rowErrorLabel(value) {
    const error = rowPercentageError(value);
    return error ? ROW_ERROR_LABELS[error]() : null;
}

/** What is left to phase on a line under one type. */
function lineUncommitted(line, type) {
    if (line.uncommitted_by_type && line.uncommitted_by_type[type] !== undefined) {
        return Number(line.uncommitted_by_type[type]) || 0;
    }
    return (line.types || []).includes(type) ? Number(line.uncommitted || 0) : 0;
}

function typeLabel(type) {
    return type === TYPE_VIK ? $t('VIK') : $t('Cash');
}

function formatMoney(value) {
    return formatToFixedMoneyWithLocale(Number(value) || 0, 2);
}

function formatPercent(value) {
    return `${Math.round(Number(value) * 100) / 100}%`;
}

/** Months are labelled as the grid's month headers label them ('MM-YYYY'). */
function monthLabel(month) {
    return String(month);
}

/**
 * The majority phasing type among the selected lines — the default for the
 * multi-line Cash/VIK switch.
 */
function majorityType(lines) {
    const vik = lines.filter((line) => (line.types || []).includes(TYPE_VIK) && !(line.types || []).includes(TYPE_CASH)).length;
    return vik > lines.length / 2 ? TYPE_VIK : TYPE_CASH;
}

function validationSchema(types) {
    const rowSchema = Yup.object({
        month: Yup.string().required(),
        percentage: Yup.string().test(
            'in-range',
            () => $t('Each month needs a percentage from 0 to 100.'),
            (value) => rowPercentageError(value) === null,
        ),
    });

    const shape = {};
    types.forEach((type) => {
        shape[type] = Yup.array()
            .of(rowSchema)
            .test(
                'hundred',
                () => $t('Allocation must total exactly 100%.'),
                (rows) => isExactly100(rows || []),
            );
    });

    return Yup.object({ schedules: Yup.object(shape) });
}

/** Ask for JSON, so an ended session is answered 401/419 rather than redirected to the login page. */
const JSON_REQUEST = { headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };

const sessionExpiredMessage = () => $t('Your session has expired — please reload the page and sign in again.');

/**
 * Whether a response is the JSON answer the dialog asked for. An expired session is
 * answered with a redirect to the login page, which the browser follows silently —
 * the HTML page that comes back must never read as a successful save.
 */
function isJsonAnswer(response, hasExpectedShape) {
    const contentType = String(response?.headers?.['content-type'] || '');
    const finalUrl = String(response?.request?.responseURL || '');
    return contentType.includes('json') && !/\/login(\/|\?|#|$)/.test(finalUrl) && hasExpectedShape(response?.data);
}

const isSaveAnswer = (response) => isJsonAnswer(response, (data) => Array.isArray(data?.data?.updated));

/** A 4xx response carrying a JSON body: the server looked at the batch and refused it. */
function isExplicitRejection(error) {
    const response = error?.response;
    return Boolean(response && response.status >= 400 && response.status < 500 && response.data && typeof response.data === 'object');
}

/**
 * A message for the user. Only our own validation messages are shown as sent; any
 * other failure (server error, timeout, lost connection) gets a plain explanation,
 * never the server's own text.
 */
function errorMessage(error) {
    const status = error?.response?.status;
    if (status === 422) {
        const errors = error.response.data?.errors;
        const firstError = errors ? Object.values(errors)[0]?.[0] : null;
        if (firstError) return firstError;
    }
    if (status === 403) return $t('You do not have permission to change this phasing.');
    if (status === 401 || status === 419) return sessionExpiredMessage();
    return $t('The phasing could not be saved. Please try again.');
}

function PhasingLineList({ title, lines, describe }) {
    if (!lines.length) return null;
    return (
        <div>
            <p className="modal__heading tw-mb-1">{title}</p>
            <ul className="tw-max-h-40 tw-overflow-y-auto tw-list-disc tw-list-inside tw-space-y-1 modal__text">
                {lines.map((line) => (
                    <li key={line.expense_id}>
                        {line.name || `#${line.expense_id}`}
                        {describe ? `: ${describe(line)}` : ''}
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * The phasing dialog body. Remounted for every opening (keyed by the app), so
 * it never carries the previous line's profile or rows.
 *
 * Single-line mode loads the line's schedule and edits % and value both ways;
 * a project's Unallocated row has a Cash/VIK switch with its own months and its
 * own 100% gate per type. Multi-line mode is % only, applied to each selected
 * line's own uncommitted total, for one chosen type.
 */
export default function CashflowPhasingDialog({ opening, showUrl, saveUrl, defaultMonth, periods: fallbackPeriods, onClose, onSaved }) {
    const isSingle = opening.mode === 'single';
    const lines = opening.lines || [];

    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(isSingle);
    const [loadError, setLoadError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [stale, setStale] = useState(false);
    const [skipped, setSkipped] = useState(null);
    const [saveProgress, setSaveProgress] = useState(null);
    const [partial, setPartial] = useState(null);

    // A save can resolve after the dialog has closed; nothing is set on it then.
    const mounted = useRef(true);
    useEffect(
        () => () => {
            mounted.current = false;
        },
        [],
    );
    const whileMounted =
        (setter) =>
        (...args) => {
            if (mounted.current) setter(...args);
        };

    useEffect(() => {
        if (!isSingle) return;
        axios
            .get(showUrl.replace('EXPENSE_ID', encodeURIComponent(lines[0].expense_id)), JSON_REQUEST)
            .then((response) => {
                if (!isJsonAnswer(response, (data) => data?.data?.types && typeof data.data.types === 'object')) {
                    whileMounted(setLoadError)(sessionExpiredMessage());
                    return;
                }
                whileMounted(setDetail)(response.data.data);
            })
            .catch(() => whileMounted(setLoadError)($t('The phasing for this line could not be loaded. Please try again.')))
            .finally(() => whileMounted(setLoading)(false));
    }, []);

    const periods = (detail && detail.periods) || fallbackPeriods || [];
    // The event's default cashflow date can sit outside the project window.
    const startMonth = clampMonth((detail && detail.default_month) || defaultMonth, periods);

    const types = useMemo(() => {
        if (!isSingle) return [TYPE_CASH, TYPE_VIK];
        return detail ? Object.keys(detail.types) : [];
    }, [detail]);

    const initialValues = useMemo(() => {
        const defaultRows = startMonth ? [{ month: startMonth, percentage: '100' }] : [];
        if (!isSingle) {
            return { activeType: majorityType(lines), schedules: { [TYPE_CASH]: defaultRows, [TYPE_VIK]: defaultRows } };
        }
        if (!detail) return null;
        const schedules = {};
        types.forEach((type) => {
            const months = detail.types[type].months || [];
            schedules[type] = months.length ? sortRows(months) : defaultRows;
        });
        return { activeType: types[0], schedules };
    }, [detail]);

    const customCount = lines.filter((line) => line.status === 'custom').length;

    /** Skipped entries named from the dialog's own lines (a deleted line comes back unnamed). */
    const withLineNames = (entries) => {
        const names = new Map(lines.map((line) => [String(line.expense_id), line.name]));
        return entries.map((entry) => ({ ...entry, name: entry.name || names.get(String(entry.expense_id)) || null }));
    };

    /**
     * A multi-line save, sent as sequential batches so a whole department stays
     * within one request's limits. Resolves with the combined result; when a batch
     * fails, `failure` says what was saved before it, what was not, and — when the
     * server gave no answer — which lines may or may not have saved.
     */
    const saveInBatches = async (schedules) => {
        const batches = chunk(lines, SAVE_BATCH_SIZE);
        const result = { updated: [], skipped: [], failure: null };
        let sent = 0;

        for (let index = 0; index < batches.length; index += 1) {
            const batch = batches[index];
            whileMounted(setSaveProgress)({ done: sent + batch.length, total: lines.length });
            try {
                // Batches go one after another: each must finish before the next is sent.
                // eslint-disable-next-line no-await-in-loop
                const response = await axios.post(
                    saveUrl,
                    {
                        mode: 'multi',
                        lines: batch.map((line) => ({ expense_id: line.expense_id, token: null })),
                        force: false,
                        schedules,
                    },
                    JSON_REQUEST,
                );
                if (!isSaveAnswer(response)) {
                    // Not our answer (the session ended): nothing in this batch or after it was saved.
                    const updatedIds = new Set(result.updated.map(String));
                    result.failure = {
                        saved: lines.filter((line) => updatedIds.has(String(line.expense_id))),
                        notSaved: batches.slice(index).flat(),
                        unknown: [],
                        error: sessionExpiredMessage(),
                        sessionExpired: true,
                    };
                    return result;
                }
                const batchResult = response.data.data;
                result.updated.push(...(batchResult.updated || []));
                result.skipped.push(...(batchResult.skipped || []));
            } catch (error) {
                const updatedIds = new Set(result.updated.map(String));
                // Only an explicit rejection proves the batch was not written; a timeout or
                // gateway error can arrive after the batch committed.
                const rejected = isExplicitRejection(error);
                result.failure = {
                    saved: lines.filter((line) => updatedIds.has(String(line.expense_id))),
                    notSaved: (rejected ? batches.slice(index) : batches.slice(index + 1)).flat(),
                    unknown: rejected ? [] : batch,
                    error: errorMessage(error),
                };
                return result;
            }
            sent += batch.length;
        }

        return result;
    };

    const submit = (values, { setSubmitting }, force = false) => {
        setSaveError(null);
        const savedTypes = isSingle ? types : [values.activeType];
        const schedules = {};
        savedTypes.forEach((type) => {
            // A month left at 0% carries nothing, so it is not sent.
            schedules[type] = withoutZeroRows(sortRows(values.schedules[type])).map((row) => ({ month: row.month, percentage: String(row.percentage).trim() }));
        });

        const finishSubmitting = () => whileMounted(setSubmitting)(false);

        const finish = (result) => {
            onSaved(result);
            if (result.skipped.length) {
                whileMounted(setSkipped)(withLineNames(result.skipped));
            } else {
                onClose();
            }
        };

        if (isSingle) {
            axios
                .post(saveUrl, { mode: 'single', lines: [{ expense_id: lines[0].expense_id, token: detail.token }], force, schedules }, JSON_REQUEST)
                .then((response) => {
                    if (!isSaveAnswer(response)) {
                        whileMounted(setSaveError)(sessionExpiredMessage());
                        return;
                    }
                    finish({ updated: [], skipped: [], ...response.data.data });
                })
                .catch((error) => {
                    if (error?.response?.status === 409 && error.response.data?.error_code === 'PHASING_STALE') {
                        whileMounted(setStale)(true);
                        return;
                    }
                    whileMounted(setSaveError)(errorMessage(error));
                })
                .finally(finishSubmitting);
            return;
        }

        saveInBatches(schedules)
            .then((result) => {
                whileMounted(setSaveProgress)(null);
                if (!result.failure) {
                    finish(result);
                    return;
                }
                // An ended session with nothing saved: say so and keep the dialog as it was.
                // Reloading the grid would only fetch the login page.
                if (result.failure.sessionExpired && result.updated.length === 0) {
                    whileMounted(setSaveError)(result.failure.error);
                    return;
                }
                // The grid reloads whatever happened, and the lines left to retry stay selected.
                const { notSaved, unknown } = result.failure;
                onSaved({
                    updated: result.updated,
                    skipped: result.skipped,
                    reselect: [...notSaved, ...unknown].map((line) => line.expense_id),
                });
                whileMounted(setPartial)({ ...result, ...result.failure, skipped: withLineNames(result.skipped) });
            })
            .finally(finishSubmitting);
    };

    const title = $t('Cashflow phasing');

    if (partial) {
        return (
            <>
                <ModalHeader title={title} close={onClose} />
                <div className="modal__form-container tw-space-y-4">
                    <Alert type="error">
                        <div className="tw-space-y-2">
                            <p className="tw-font-semibold">
                                {$t('The save stopped part-way: {{saved}} of {{total}} lines were saved.', {
                                    saved: partial.saved.length,
                                    total: lines.length,
                                })}
                            </p>
                            <p>{partial.error}</p>
                        </div>
                    </Alert>
                    {partial.unknown.length > 0 && (
                        <div className="tw-space-y-1">
                            <p className="modal__text">{$t('These lines may or may not have saved — the grid now shows the current figures.')}</p>
                            <PhasingLineList title={$t('Status unknown ({{count}})', { count: partial.unknown.length })} lines={partial.unknown} />
                        </div>
                    )}
                    <PhasingLineList title={$t('Not saved ({{count}})', { count: partial.notSaved.length })} lines={partial.notSaved} />
                    <PhasingLineList title={$t('Saved ({{count}})', { count: partial.saved.length })} lines={partial.saved} />
                    {partial.skipped.length > 0 && (
                        <PhasingLineList
                            title={$t('Skipped ({{count}})', { count: partial.skipped.length })}
                            lines={partial.skipped}
                            describe={(line) => (SKIP_REASON_LABELS[line.reason] || (() => line.reason))()}
                        />
                    )}
                    <div className="modal__footer">
                        <Button variant="secondary" onClick={onClose}>
                            {$t('Close')}
                        </Button>
                    </div>
                </div>
            </>
        );
    }

    if (skipped) {
        return (
            <>
                <ModalHeader title={title} close={onClose} />
                <div className="modal__form-container">
                    <Alert type="warning">
                        <div>
                            <p className="tw-font-semibold tw-mb-1">{$t('{{count}} line(s) were not updated:', { count: skipped.length })}</p>
                            <ul className="tw-list-disc tw-list-inside tw-space-y-1">
                                {skipped.map((line) => (
                                    <li key={line.expense_id}>
                                        {line.name || `#${line.expense_id}`}: {(SKIP_REASON_LABELS[line.reason] || (() => line.reason))()}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Alert>
                    <div className="modal__footer">
                        <Button variant="secondary" onClick={onClose}>
                            {$t('Close')}
                        </Button>
                    </div>
                </div>
            </>
        );
    }

    if (!initialValues) {
        return (
            <>
                <ModalHeader title={title} close={onClose} />
                <div className="modal__form-container">
                    {loading && <LoadingSpinner />}
                    {loadError && <Alert type="error">{loadError}</Alert>}
                </div>
            </>
        );
    }

    return (
        <Formik
            initialValues={initialValues}
            validate={(values) => {
                // Single mode gates every type the line carries; multi mode only the chosen one.
                try {
                    validationSchema(isSingle ? types : [values.activeType]).validateSync(values, { abortEarly: true });
                    return {};
                } catch (error) {
                    return { schedules: error.message };
                }
            }}
            onSubmit={submit}
        >
            {(formik) => (
                <PhasingForm
                    formik={formik}
                    title={title}
                    isSingle={isSingle}
                    types={types}
                    detail={detail}
                    lines={lines}
                    periods={periods}
                    startMonth={startMonth}
                    customCount={customCount}
                    saveError={saveError}
                    saveProgress={saveProgress}
                    stale={stale}
                    onForceSave={() => {
                        setStale(false);
                        formik.setSubmitting(true);
                        submit(formik.values, formik, true);
                    }}
                    onClose={onClose}
                />
            )}
        </Formik>
    );
}

function PhasingForm({
    formik,
    title,
    isSingle,
    types,
    detail,
    lines,
    periods,
    startMonth,
    customCount,
    saveError,
    saveProgress,
    stale,
    onForceSave,
    onClose,
}) {
    const { values, errors, submitCount, setFieldValue, isSubmitting, handleSubmit } = formik;
    const activeType = values.activeType;
    const rows = values.schedules[activeType] || [];

    const [profile, setProfile] = useState(PROFILE_MONTHLY);
    const [profileStart, setProfileStart] = useState(startMonth);
    const [profileCount, setProfileCount] = useState(3);
    const [monthToAdd, setMonthToAdd] = useState('');
    // What the user typed into a % or value field, shown as typed until that row
    // changes some other way. Untouched fields show the stored figure, rounded.
    const [typedPercentages, setTypedPercentages] = useState({});
    const [typedValues, setTypedValues] = useState({});

    const multiLines = lines.filter((line) => (line.types || []).includes(activeType));
    const total = isSingle
        ? Number(detail?.types?.[activeType]?.uncommitted_total || 0)
        : multiLines.reduce((sum, line) => sum + lineUncommitted(line, activeType), 0);

    const clearTyped = () => {
        setTypedPercentages({});
        setTypedValues({});
    };

    const setRows = (next) => setFieldValue(`schedules.${activeType}`, sortRows(next));

    const applyProfile = () => {
        clearTyped();
        setRows(buildProfile(profile, profileStart, profileCount, periods));
    };

    const typedKey = (month) => `${activeType}:${month}`;

    const updatePercentage = (month, percentage) => {
        setTypedPercentages((prev) => ({ ...prev, [typedKey(month)]: percentage }));
        setTypedValues((prev) => ({ ...prev, [typedKey(month)]: undefined }));
        setRows(rows.map((row) => (row.month === month ? { ...row, percentage } : row)));
    };

    const updateValue = (month, value) => {
        setTypedValues((prev) => ({ ...prev, [typedKey(month)]: value }));
        setTypedPercentages((prev) => ({ ...prev, [typedKey(month)]: undefined }));
        setRows(rows.map((row) => (row.month === month ? { ...row, percentage: valueToPercentage(value, total) } : row)));
    };

    const removeMonth = (month) => setRows(rows.filter((row) => row.month !== month));

    const addMonth = () => {
        if (!monthToAdd) return;
        setRows([...rows, { month: monthToAdd, percentage: '0' }]);
        setMonthToAdd('');
    };

    const listed = new Set(rows.map((row) => row.month));
    const addable = periods.filter((period) => !listed.has(period));

    const { allocated, remaining } = allocation(rows);
    const exact = isExactly100(rows);
    const rowValues = valuesForRows(sortRows(rows), total);
    const typesComplete = (isSingle ? types : [activeType]).every((type) => {
        const typeRows = values.schedules[type] || [];
        return rowsInRange(typeRows) && isExactly100(typeRows);
    });
    const canSave = typesComplete && (isSingle || multiLines.length > 0);
    const formError = submitCount > 0 && typeof errors.schedules === 'string' ? errors.schedules : null;
    const progress = Math.max(0, Math.min(100, allocated));

    const subjectName = isSingle ? detail?.name || lines[0]?.name : $t('{{count}} lines', { count: lines.length });
    const subtitle = `${subjectName} · ${$t('{{value}} uncommitted', { value: formatMoney(total) })}`;
    const unitLabel = profile === PROFILE_QUARTERLY ? $t('quarters') : $t('months');

    let remainingText;
    if (exact) {
        remainingText = $t('Fully allocated');
    } else if (remaining > 0) {
        remainingText = isSingle
            ? $t('{{percent}} remaining · {{value}}', { percent: formatPercent(remaining), value: formatMoney((remaining * total) / 100) })
            : $t('{{percent}} remaining', { percent: formatPercent(remaining) });
    } else {
        remainingText = $t('Over-allocated by {{percent}}', { percent: formatPercent(-remaining) });
    }

    return (
        <>
            <ModalHeader title={title} subtitle={subtitle} close={onClose} />
            <div className="modal__form-container tw-space-y-4">
                {!isSingle && customCount > 0 && (
                    <Alert type="warning">
                        {customCount === 1
                            ? $t('{{custom}} of {{total}} selected lines has custom phasing. Applying will replace it.', {
                                  custom: customCount,
                                  total: lines.length,
                              })
                            : $t('{{custom}} of {{total}} selected lines have custom phasing. Applying will replace it.', {
                                  custom: customCount,
                                  total: lines.length,
                              })}
                    </Alert>
                )}

                {(types.length > 1 || !isSingle) && (
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                        <span className="modal__text">{isSingle ? $t('Phase by') : $t('Apply to')}</span>
                        <SegmentedControl
                            value={activeType}
                            onChange={(type) => {
                                clearTyped();
                                setFieldValue('activeType', type);
                            }}
                            options={[
                                { value: TYPE_CASH, label: typeLabel(TYPE_CASH) },
                                { value: TYPE_VIK, label: typeLabel(TYPE_VIK) },
                            ]}
                        />
                    </div>
                )}

                {!isSingle && multiLines.length < lines.length && (
                    <p className="modal__text">
                        {multiLines.length === 0
                            ? $t('None of the selected lines are {{type}}.', { type: typeLabel(activeType) })
                            : $t('{{count}} of {{total}} selected lines are {{type}}; the rest will be skipped.', {
                                  count: multiLines.length,
                                  total: lines.length,
                                  type: typeLabel(activeType),
                              })}
                    </p>
                )}

                <div className="tw-space-y-1.5">
                    <div className="tw-flex tw-flex-nowrap tw-items-center tw-gap-2 tw-whitespace-nowrap">
                        <select
                            className="tw-border-0 select-input !tw-w-auto"
                            value={profile}
                            onChange={(e) => setProfile(e.target.value)}
                            aria-label={$t('Phasing profile')}
                        >
                            <option value={PROFILE_MONTHLY}>{$t('Monthly')}</option>
                            <option value={PROFILE_QUARTERLY}>{$t('Quarterly')}</option>
                            <option value={PROFILE_SINGLE}>{$t('Single month')}</option>
                        </select>
                        <span className="modal__text">{$t('from')}</span>
                        <select
                            className="tw-border-0 select-input !tw-w-auto"
                            value={profileStart}
                            onChange={(e) => setProfileStart(e.target.value)}
                            aria-label={$t('Start month')}
                        >
                            {periods.map((period) => (
                                <option key={period} value={period}>
                                    {monthLabel(period)}
                                </option>
                            ))}
                        </select>
                        {profile !== PROFILE_SINGLE && (
                            <>
                                <span className="modal__text">{$t('over')}</span>
                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    className="text-input tw-border-0 tw-w-16"
                                    value={profileCount}
                                    onChange={(e) => setProfileCount(e.target.value)}
                                    aria-label={profile === PROFILE_QUARTERLY ? $t('Number of quarters') : $t('Number of months')}
                                />
                                <span className="modal__text">{unitLabel}</span>
                            </>
                        )}
                        <Button variant="primary" small onClick={applyProfile}>
                            {$t('Apply')}
                        </Button>
                        <button
                            type="button"
                            className="tw-ms-auto tw-text-sm tw-text-blue tw-whitespace-nowrap hover:tw-underline disabled:tw-opacity-40 disabled:tw-no-underline"
                            onClick={() => {
                                clearTyped();
                                setRows(scaleTo100(rows));
                            }}
                            disabled={rows.length === 0}
                        >
                            {$t('Scale to 100%')}
                        </button>
                    </div>
                    <p className="tw-text-2xs tw-text-charcoal-75">
                        {isSingle
                            ? $t('Enter % or value per month; the other is calculated. Applying a profile replaces the current months.')
                            : $t("Multiple lines selected: % only. Each % applies against each line's own uncommitted total.")}
                    </p>
                </div>

                <div className="tw-max-h-80 tw-overflow-y-auto">
                    <table className="tw-w-full tw-text-sm">
                        <thead>
                            <tr className="tw-border-b tw-border-grey">
                                <th className="modal__heading tw-text-start tw-py-2">{$t('Month')}</th>
                                <th className="modal__heading tw-text-end tw-py-2 tw-w-32">%</th>
                                <th className="modal__heading tw-text-end tw-py-2 tw-w-40">{$t('Value')}</th>
                                <th className="tw-w-8" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const key = typedKey(row.month);
                                const typedPercentage = typedPercentages[key];
                                const typedValue = typedValues[key];
                                const rowError = rowErrorLabel(row.percentage);
                                return (
                                    <tr key={row.month} className="tw-border-b tw-border-grey tw-align-top">
                                        <td className="modal__text tw-py-2">{monthLabel(row.month)}</td>
                                        <td className="tw-py-2">
                                            <div className="tw-flex tw-items-center tw-justify-end tw-gap-x-1.5">
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    aria-label={$t('Percentage for {{month}}', { month: monthLabel(row.month) })}
                                                    aria-invalid={rowError ? 'true' : undefined}
                                                    className={classNames(
                                                        'text-input tw-w-24 tw-text-end',
                                                        rowError ? 'tw-border tw-border-red' : 'tw-border-0',
                                                    )}
                                                    value={typedPercentage !== undefined ? typedPercentage : displayPercentage(row.percentage)}
                                                    onChange={(e) => updatePercentage(row.month, e.target.value)}
                                                />
                                                <span className="tw-text-charcoal-75">%</span>
                                            </div>
                                            {rowError && <p className="modal__input-error-text tw-text-end">{rowError}</p>}
                                        </td>
                                        <td className="tw-py-2">
                                            {isSingle ? (
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    aria-label={$t('Value for {{month}}', { month: monthLabel(row.month) })}
                                                    className="text-input tw-border-0 tw-w-36 tw-text-end tw-ms-auto tw-block"
                                                    value={typedValue !== undefined ? typedValue : String(rowValues[row.month] ?? 0)}
                                                    onChange={(e) => updateValue(row.month, e.target.value)}
                                                />
                                            ) : (
                                                <span className="tw-block tw-text-end modal__text">{formatMoney(rowValues[row.month] ?? 0)}</span>
                                            )}
                                        </td>
                                        <td className="tw-py-2 tw-text-end">
                                            <button
                                                type="button"
                                                className="tw-text-charcoal-75 hover:tw-text-red"
                                                aria-label={$t('Remove {{month}}', { month: monthLabel(row.month) })}
                                                onClick={() => removeMonth(row.month)}
                                            >
                                                <span aria-hidden="true" className="tw-text-base tw-leading-none">
                                                    ×
                                                </span>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="modal__text tw-py-3 tw-text-center tw-text-charcoal-75">
                                        {$t('No months allocated. Apply a profile or add a month.')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap">
                    <select
                        className="tw-border-0 select-input !tw-w-auto tw-min-w-[10rem]"
                        value={monthToAdd}
                        onChange={(e) => setMonthToAdd(e.target.value)}
                        aria-label={$t('Add a month')}
                    >
                        <option value="">{$t('Add a month…')}</option>
                        {addable.map((period) => (
                            <option key={period} value={period}>
                                {monthLabel(period)}
                            </option>
                        ))}
                    </select>
                    <Button variant="outline" small className="tw-whitespace-nowrap" onClick={addMonth} disabled={!monthToAdd}>
                        {$t('+ Add month')}
                    </Button>
                </div>

                <div className="tw-space-y-2">
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-text-sm">
                        <span>
                            {$t('Allocated {{percent}} · {{value}}', { percent: formatPercent(allocated), value: formatMoney((allocated * total) / 100) })}
                        </span>
                        <span
                            className={classNames('tw-font-semibold', {
                                'tw-text-dark-green': exact,
                                'tw-text-charcoal-75': !exact && remaining > 0,
                                'tw-text-red': !exact && remaining < 0,
                            })}
                        >
                            {remainingText}
                        </span>
                    </div>
                    <div className="tw-h-1.5 tw-w-full tw-rounded-full tw-bg-grey tw-overflow-hidden">
                        <div
                            className={classNames('tw-h-full tw-rounded-full', exact ? 'tw-bg-green' : allocated > 100 ? 'tw-bg-red' : 'tw-bg-blue')}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    {isSingle && types.length > 1 && !typesComplete && exact && (
                        <p className="modal__input-error-text">{$t('The other type must also total exactly 100% before saving.')}</p>
                    )}
                    {formError && <p className="modal__input-error-text">{formError}</p>}
                </div>

                {saveProgress && <p className="modal__text">{$t('Saving {{done}} of {{total}}…', saveProgress)}</p>}
                {saveError && <Alert type="error">{saveError}</Alert>}
                {stale && (
                    <Alert type="warning">
                        <div className="tw-flex tw-flex-col tw-gap-2">
                            <span>{$t('Someone else changed this line’s phasing since you opened it. Save anyway to overwrite their change.')}</span>
                            <div>
                                <Button variant="primary" small onClick={onForceSave}>
                                    {$t('Save anyway')}
                                </Button>
                            </div>
                        </div>
                    </Alert>
                )}

                <div className="modal__footer">
                    <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                        {$t('Cancel')}
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting || !canSave} loading={isSubmitting}>
                        {isSingle ? $t('Save') : $t('Apply to {{count}} lines', { count: multiLines.length })}
                    </Button>
                </div>
            </div>
        </>
    );
}
